import type { editor } from "monaco-editor";

import { Editor, Monaco } from "@monaco-editor/react";
import { FC, useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { useMonacoPicker } from "../../hooks/useMonacoPicker";
import {
  handleEditorValidation as handleValidation,
  setupValidators,
} from "../../utils/monaco-editor-helpers";
import DateSnippetPopup from "./DatePopupPicker";
import EntityPopupPicker from "./EntityPopupPicker";
import LiquidSnippetPopup from "./LiquidSnippetPopup";

const EntityAwareIntellisenseEditor: FC<{
  bordered?: boolean;
  className?: string;
  fieldName?: null | string;
  language?: "json" | "liquid" | "plaintext";
  noMargin?: boolean;
  onChange(value: string): void;
  onPaste?(pastedText: string): { replacementText?: string } | false;
  readOnly?: boolean;
  singleLine?: boolean;
  value: string;
}> = ({
  bordered = true,
  className,
  fieldName = null,
  language = "plaintext",
  noMargin = false,
  onChange,
  onPaste,
  readOnly,
  singleLine = false,
  value,
}) => {
  const { clearErrors, setError } = useFormContext();

  const {
    closePicker,
    handleEditorMount: handlePickerMount,
    insertText,
    pickerState,
    setupContextMenu,
  } = useMonacoPicker();

  useEffect(() => {
    if (window._refreshEntityList) {
      window._refreshEntityList();
    }
  }, []);

  const handleEditorMount = useCallback(
    (editorInstance: editor.IStandaloneCodeEditor, monacoInstance: Monaco) => {
      handlePickerMount(editorInstance, monacoInstance);
      setupContextMenu(editorInstance, monacoInstance);
      setupValidators(editorInstance, monacoInstance, { language });

      // Handle paste events if onPaste callback is provided
      if (onPaste) {
        const model = editorInstance.getModel();
        if (model) {
          editorInstance.onDidPaste((e) => {
            const pastedText = model.getValueInRange(e.range);
            if (!pastedText) return;

            const result = onPaste(pastedText);
            if (!result) return; // not handled -> keep Monaco's paste

            // handled -> replace the pasted text with the processed text (or empty)
            editorInstance.executeEdits("paste-transform", [
              {
                forceMoveMarkers: true,
                range: e.range,
                text: result.replacementText ?? "",
              },
            ]);
          });
        }
      }
    },
    [language, handlePickerMount, onPaste, setupContextMenu],
  );
  const handleEditorValidation = useCallback(
    (markers: editor.IMarker[]) => {
      handleValidation(markers, {
        clearErrors,
        fieldName,
        setError,
      });
    },
    [clearErrors, fieldName, setError],
  );

  const marginRemoval = noMargin
    ? {
        folding: false,
        glyphMargin: false,
        lineDecorationsWidth: 0,
        lineNumbersMinChars: 0,
      }
    : {};

  return (
    <>
      <Editor
        className={`${bordered ? "monaco-bordered" : ""} ${className ?? ""}`}
        height={singleLine ? "36px" : "100%"}
        language={language}
        onChange={(v) => onChange(v || "")}
        onMount={handleEditorMount}
        onValidate={handleEditorValidation}
        options={{
          automaticLayout: true,
          fontSize: singleLine ? 14 : undefined,
          lineNumbers: "off",
          minimap: { enabled: false },
          padding: { bottom: 4, top: 4 },
          quickSuggestions: false,
          readOnly: readOnly || false,
          scrollbar: {
            horizontal: "hidden",
            vertical: singleLine ? "hidden" : "auto",
          },
          scrollBeyondLastLine: !singleLine,
          suggestOnTriggerCharacters: true,
          wordBasedSuggestions: "off",
          wordWrap: "off",
          ...marginRemoval,
          fontFamily: singleLine
            ? "ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
            : undefined,
          links: false,
          "semanticHighlighting.enabled": true,
        }}
        theme="miru-dark"
        value={value}
        width="100%"
      />

      {pickerState.open && pickerState.type === "entity" && (
        <EntityPopupPicker
          left={pickerState.left}
          onClose={closePicker}
          onSelect={(entity) => {
            insertText(`<<${entity.qualifiedName}>>`, pickerState.range);
          }}
          top={pickerState.top}
        />
      )}

      {pickerState.open && pickerState.type === "date" && (
        <DateSnippetPopup
          left={pickerState.left}
          onClose={closePicker}
          onInsert={(snippet) => {
            insertText(snippet, pickerState.range);
          }}
          top={pickerState.top}
        />
      )}

      {pickerState.open && pickerState.type === "liquid" && (
        <LiquidSnippetPopup
          left={pickerState.left}
          onClose={closePicker}
          onInsert={(snippet) => insertText(snippet, pickerState.range)}
          top={pickerState.top}
        />
      )}
    </>
  );
};

export default EntityAwareIntellisenseEditor;
